import { withStyleNonce } from '@/lib/csp-nonce';
import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowClockwise,
  CaretLeft,
  CaretRight,
  ChatCircleText,
  CheckCircle,
  Info,
  PencilSimple,
  SealCheck,
  WarningCircle,
} from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import { Card, CardContent } from '@/components/ui-kit/card';
import { Input } from '@/components/ui-kit/input';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { Switch } from '@/components/ui-kit/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { useToast } from '@/hooks/use-toast';
import { HttpError } from '@/lib/https';
import { cn } from '@/lib/utils';

import {
  OPT_IN_PENDING_QUERY_KEY,
  useGetOptInSettings,
  useGetPendingOptIns,
  useResendOptIn,
  useUpdateOptInSettings,
} from '../hooks/use-opt-in-settings';
import { useGetBrandingSettings } from '../hooks/use-compliance-settings';
import { useGetSystemEmail, usePreviewSystemEmail } from '../hooks/use-system-emails';
import type {
  OptInFormErrors,
  OptInFormState,
  PendingOptInItem,
  PendingOptInsResult,
} from '../types/opt-in-settings.types';

const URL_PATTERN = /^https?:\/\/.+/i;
const PAGE_SIZE = 50;

const initialForm: OptInFormState = {
  emailDoubleOptInEnabled: false,
  tokenTtlHours: '72',
  resendCooldownMinutes: '15',
  confirmedRedirectUrl: '',
};

const validateForm = (form: OptInFormState): OptInFormErrors => {
  const errors: OptInFormErrors = {};
  const ttl = Number(form.tokenTtlHours);
  const cooldown = Number(form.resendCooldownMinutes);
  const redirectUrl = form.confirmedRedirectUrl.trim();

  if (!form.tokenTtlHours.trim() || !Number.isInteger(ttl) || ttl <= 0) {
    errors.tokenTtlHours = "'tokenTtlHours' must be a whole number of hours greater than 0.";
  }

  if (!form.resendCooldownMinutes.trim() || !Number.isInteger(cooldown) || cooldown < 0) {
    errors.resendCooldownMinutes = "'resendCooldownMinutes' must be 0 or more.";
  }

  if (redirectUrl) {
    if (
      !URL_PATTERN.test(redirectUrl) ||
      redirectUrl.toLowerCase().startsWith('javascript:') ||
      redirectUrl.toLowerCase().startsWith('data:')
    ) {
      errors.confirmedRedirectUrl =
        "'confirmedRedirectUrl' must be a full URL starting with http:// or https://, or blank.";
    }
  }

  return errors;
};

const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const FieldError = ({ children }: { children?: string }) =>
  children ? (
    <p className="mt-1 flex items-center gap-1 text-xs text-destructive">
      <WarningCircle size={13} />
      {children}
    </p>
  ) : null;

export const OptInSettingsPage = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [form, setForm] = useState<OptInFormState>(initialForm);
  const [formInitialized, setFormInitialized] = useState(false);
  const [errors, setErrors] = useState<OptInFormErrors>({});
  const [savedNote, setSavedNote] = useState('');
  const [serverErrorMessage, setServerErrorMessage] = useState<string | null>(null);

  const [offset, setOffset] = useState(0);
  const [resendingIds, setResendingIds] = useState<Set<string>>(new Set());
  const [cooldowns, setCooldowns] = useState<Record<string, number>>({});

  const settingsQuery = useGetOptInSettings();
  const pendingQuery = useGetPendingOptIns({ limit: PAGE_SIZE, offset });
  const updateMutation = useUpdateOptInSettings();
  const resendMutation = useResendOptIn();

  const brandingQuery = useGetBrandingSettings();
  const defaultLocale = brandingQuery.data?.defaultLocale || 'en';
  const previewQuery = usePreviewSystemEmail('contact.double-optin', defaultLocale);
  const emailDetailQuery = useGetSystemEmail('contact.double-optin', defaultLocale);

  const previewSubject =
    previewQuery.data?.subject ||
    emailDetailQuery.data?.subject ||
    'Please confirm your subscription';
  const previewHtml =
    previewQuery.data?.html ||
    emailDetailQuery.data?.bodyHtml ||
    '<div style="font-family:Inter,Arial,sans-serif;font-size:14px;color:#1a1a1a;padding:16px">' +
      '<p>Thanks for signing up to hear from us. Please confirm that this is your email address.</p>' +
      '<a href="#" style="display:inline-block;margin-top:12px;padding:10px 20px;background:#0066B2;color:#fff;text-decoration:none;border-radius:6px;font-weight:500">Yes, subscribe me</a>' +
      '</div>';
  const isCustomised = emailDetailQuery.data?.isCustomised ?? false;

  // Populate form state when initial settings are loaded
  useEffect(() => {
    if (settingsQuery.data && !formInitialized) {
      setForm({
        emailDoubleOptInEnabled: settingsQuery.data.emailDoubleOptInEnabled,
        tokenTtlHours: String(settingsQuery.data.tokenTtlHours),
        resendCooldownMinutes: String(settingsQuery.data.resendCooldownMinutes),
        confirmedRedirectUrl: settingsQuery.data.confirmedRedirectUrl ?? '',
      });
      setFormInitialized(true);
    }
  }, [settingsQuery.data, formInitialized]);

  // Tick cooldown countdowns every second
  useEffect(() => {
    const hasActiveCooldown = Object.values(cooldowns).some((s) => s > 0);
    if (!hasActiveCooldown) return;

    const interval = setInterval(() => {
      setCooldowns((prev) => {
        let changed = false;
        const next: Record<string, number> = {};
        for (const [id, sec] of Object.entries(prev)) {
          if (sec > 1) {
            next[id] = sec - 1;
            changed = true;
          } else if (sec === 1) {
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [cooldowns]);

  const updateField = <K extends keyof OptInFormState>(field: K, value: OptInFormState[K]) => {
    setForm((current) => ({ ...current, [field]: value }));
    setSavedNote('');
    setServerErrorMessage(null);
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = validateForm(form);
    setErrors(nextErrors);
    setServerErrorMessage(null);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    try {
      await updateMutation.mutateAsync({
        emailDoubleOptInEnabled: form.emailDoubleOptInEnabled,
        tokenTtlHours: Number(form.tokenTtlHours),
        resendCooldownMinutes: Number(form.resendCooldownMinutes),
        // Send "" to clear, or trimmed url, per partial PATCH semantics in §4.2
        confirmedRedirectUrl: form.confirmedRedirectUrl.trim(),
      });

      setSavedNote('Saved just now.');
      toast({
        title: 'Settings saved',
        description: 'Double opt-in settings have been updated.',
      });
    } catch (err) {
      if (err instanceof HttpError && err.status === 400) {
        const serverErrors = (err.error?.errors as string[]) ?? [];
        const fieldErrors: OptInFormErrors = {};
        for (const errStr of serverErrors) {
          const colonIndex = errStr.indexOf(': ');
          if (colonIndex !== -1) {
            const prop = errStr.slice(0, colonIndex).toLowerCase();
            const message = errStr.slice(colonIndex + 2);
            if (prop.includes('tokenttl')) {
              fieldErrors.tokenTtlHours = message;
            } else if (prop.includes('resendcooldown')) {
              fieldErrors.resendCooldownMinutes = message;
            } else if (prop.includes('confirmedredirect') || prop.includes('confirmredirect')) {
              fieldErrors.confirmedRedirectUrl = message;
            }
          }
        }
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors);
          return;
        }
      }

      setServerErrorMessage(
        'Something went wrong. Your changes have not been lost - try Save again.'
      );
    }
  };

  const handleResend = async (row: PendingOptInItem) => {
    const contactId = row.contactId || (row as any).id || (row as any).itemId;
    if (!contactId || resendingIds.has(contactId) || (cooldowns[contactId] ?? 0) > 0) return;

    setResendingIds((prev) => new Set(prev).add(contactId));

    try {
      const idempotencyKey = crypto.randomUUID();
      const result = await resendMutation.mutateAsync({
        contactId,
        idempotencyKey,
        channel: 'email',
      });

      // 202 Accepted: patch table row in-place
      queryClient.setQueryData<PendingOptInsResult>(
        [...OPT_IN_PENDING_QUERY_KEY, { limit: PAGE_SIZE, offset }],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.map((item) =>
              item.contactId === contactId
                ? {
                    ...item,
                    sentAt: result?.resentAt || new Date().toISOString(),
                    resendCount: result?.resendCount ?? item.resendCount + 1,
                  }
                : item
            ),
          };
        }
      );

      toast({
        title: 'Confirmation resent',
        description: `A confirmation email has been resent to ${row.email}.`,
      });
    } catch (err: any) {
      const status =
        err instanceof HttpError
          ? err.status
          : typeof err === 'object' && err !== null && 'status' in err
            ? Number(err.status)
            : err?.response?.status;

      if (status === 404) {
        // Contact not found or deleted: remove row from table
        queryClient.setQueryData<PendingOptInsResult>(
          [...OPT_IN_PENDING_QUERY_KEY, { limit: PAGE_SIZE, offset }],
          (old) => {
            if (!old) return old;
            return {
              ...old,
              items: old.items.filter((item) => item.contactId !== contactId),
              pageInfo: {
                ...old.pageInfo,
                total: Math.max(0, old.pageInfo.total - 1),
                count: Math.max(0, old.pageInfo.count - 1),
              },
            };
          }
        );
        toast({
          variant: 'destructive',
          title: 'Contact not found',
          description: 'This contact no longer exists.',
        });
        return;
      }

      if (status === 409) {
        // Already confirmed: refresh table
        toast({
          title: 'Already confirmed',
          description: 'This contact has already confirmed.',
        });
        queryClient.invalidateQueries({ queryKey: OPT_IN_PENDING_QUERY_KEY });
        return;
      }

      if (status === 429) {
        const errData = err?.error?.data ?? err?.response?.data?.data ?? err?.error;
        let retryAfter = errData?.retryAfterSeconds;
        if (!retryAfter && typeof err?.message === 'string') {
          const match = err.message.match(/(\d+)\s*sec/i);
          if (match) retryAfter = parseInt(match[1], 10);
        }
        const cooldownSec = retryAfter ?? 60;
        setCooldowns((prev) => ({ ...prev, [contactId]: cooldownSec }));
        toast({
          title: 'Too soon to resend',
          description: `Too soon to resend — try again in ${cooldownSec}s.`,
        });
        return;
      }

      const rawMessage =
        err?.error?.message ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to resend confirmation email.';

      toast({
        variant: 'destructive',
        title: rawMessage,
        description: rawMessage,
      });
    } finally {
      setResendingIds((prev) => {
        const next = new Set(prev);
        next.delete(contactId);
        return next;
      });
    }
  };

  const isInitialLoading =
    (settingsQuery.isLoading && !settingsQuery.data) ||
    (pendingQuery.isLoading && !pendingQuery.data);

  const isInitialError =
    (settingsQuery.isError && !settingsQuery.data) || (pendingQuery.isError && !pendingQuery.data);

  if (isInitialError) {
    return (
      <div className="mx-auto flex h-full w-full max-w-[720px] flex-col gap-5 pb-8">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">Double opt-in</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Require a confirmed click before a subscriber counts as subscribed.
          </p>
        </div>

        <Card className="rounded-lg border-destructive/20 bg-destructive/5 p-8 text-center">
          <WarningCircle size={36} className="mx-auto text-destructive" />
          <h2 className="mt-3 text-lg font-semibold text-foreground">Failed to load settings</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            There was a problem loading double opt-in settings from the server.
          </p>
          <Button
            className="mt-5"
            variant="outline"
            onClick={() => {
              settingsQuery.refetch();
              pendingQuery.refetch();
            }}
          >
            <ArrowClockwise size={16} className="mr-2" />
            Retry
          </Button>
        </Card>
      </div>
    );
  }

  if (isInitialLoading) {
    return (
      <div className="mx-auto flex h-full w-full max-w-[720px] flex-col gap-5 pb-8">
        <div>
          <Skeleton className="h-9 w-48" />
          <Skeleton className="mt-2 h-4 w-96" />
        </div>
        <Skeleton className="h-20 w-full rounded-lg" />
        <Card className="rounded-lg border-neutral-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-6 w-10" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
            <Skeleton className="h-16 w-full" />
          </div>
        </Card>
        <Skeleton className="h-28 w-full rounded-lg" />
        <Card className="rounded-lg border-neutral-200 bg-white p-6 shadow-sm">
          <Skeleton className="h-40 w-full" />
        </Card>
      </div>
    );
  }

  const pendingItems = pendingQuery.data?.items ?? [];
  const pageInfo = pendingQuery.data?.pageInfo;
  const hasPrev = pageInfo ? pageInfo.hasPrev : offset > 0;
  const hasNext = pageInfo ? pageInfo.hasNext : false;
  const totalCount = pageInfo?.total ?? 0;

  return (
    <div className="w-full">
      <div className="mx-auto flex w-full max-w-[720px] flex-col gap-5 pb-8">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">Double opt-in</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Require a confirmed click before a subscriber counts as subscribed.
          </p>
        </div>

        <div className="flex items-start gap-2.5 rounded-lg border border-[#B9DEF8] bg-[#E6F0F8] px-3.5 py-3 text-sm leading-5 text-[#003F6F]">
          <Info size={16} className="mt-0.5 shrink-0 text-primary" />
          <p>
            While this is on, a channel landing in <span className="font-mono">pending</span> stays
            there until the contact confirms. A pending contact is <strong>not sendable</strong> —
            every send counts them as a <strong>skip</strong>, with that reason, not as a recipient.
          </p>
        </div>

        <form className="flex flex-col" onSubmit={handleSubmit} noValidate>
          <div className="flex flex-col gap-5">
            <Card className="rounded-lg border-neutral-200 bg-white shadow-sm">
              <CardContent className="flex flex-col gap-4 p-5 sm:p-6">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">Email double opt-in</h2>
                    <p className="mt-0.5 text-sm text-muted-foreground">
                      A new subscriber must confirm by email before they count as subscribed.
                    </p>
                  </div>
                  <Switch
                    checked={form.emailDoubleOptInEnabled}
                    onCheckedChange={(checked) => updateField('emailDoubleOptInEnabled', checked)}
                    aria-label="Enable email double opt-in"
                  />
                </div>

                <div className="grid gap-4 border-t border-neutral-100 pt-4 sm:grid-cols-2">
                  <div>
                    <label
                      className="mb-1.5 block text-sm font-medium text-foreground"
                      htmlFor="tokenTtlHours"
                    >
                      Confirmation link expires after (hours)
                    </label>
                    <Input
                      id="tokenTtlHours"
                      type="number"
                      min={1}
                      step={1}
                      value={form.tokenTtlHours}
                      onChange={(event) => updateField('tokenTtlHours', event.target.value)}
                      className={cn(
                        errors.tokenTtlHours && 'border-destructive focus-visible:ring-destructive'
                      )}
                      placeholder="72"
                    />
                    <FieldError>{errors.tokenTtlHours}</FieldError>
                  </div>

                  <div>
                    <label
                      className="mb-1.5 block text-sm font-medium text-foreground"
                      htmlFor="resendCooldownMinutes"
                    >
                      Resend cooldown (minutes)
                    </label>
                    <Input
                      id="resendCooldownMinutes"
                      type="number"
                      min={0}
                      step={1}
                      value={form.resendCooldownMinutes}
                      onChange={(event) => updateField('resendCooldownMinutes', event.target.value)}
                      className={cn(
                        errors.resendCooldownMinutes &&
                          'border-destructive focus-visible:ring-destructive'
                      )}
                      placeholder="15"
                    />
                    <FieldError>{errors.resendCooldownMinutes}</FieldError>
                  </div>
                </div>

                <div>
                  <label
                    className="mb-1.5 block text-sm font-medium text-foreground"
                    htmlFor="confirmedRedirectUrl"
                  >
                    Confirmation redirect URL
                  </label>
                  <Input
                    id="confirmedRedirectUrl"
                    type="text"
                    value={form.confirmedRedirectUrl}
                    onChange={(event) => updateField('confirmedRedirectUrl', event.target.value)}
                    className={cn(
                      errors.confirmedRedirectUrl &&
                        'border-destructive focus-visible:ring-destructive'
                    )}
                    placeholder="https://example.com/thanks-for-confirming"
                  />
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    Where the contact lands right after confirming. Leave blank to show the default
                    confirmation page.
                  </p>
                  <FieldError>{errors.confirmedRedirectUrl}</FieldError>
                </div>
              </CardContent>
            </Card>

            {/* Confirmation email — read-only preview, edited via System emails */}
            <Card className="rounded-lg border-neutral-200 bg-white shadow-sm">
              <CardContent className="flex flex-col gap-4 p-5 sm:p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">Confirmation email</h2>
                    <p className="mt-0.5 text-sm text-muted-foreground">
                      What a new subscriber receives. It is edited in System emails, one version per
                      language.
                    </p>
                  </div>
                  <Button type="button" variant="outline" size="sm" asChild>
                    <Link to="/settings/system-emails?key=contact.double-optin">
                      <PencilSimple size={15} />
                      Edit email
                    </Link>
                  </Button>
                </div>

                {/* Preview box: subject row + iframe body */}
                <div className="overflow-hidden rounded-md border border-neutral-200">
                  <div className="flex flex-wrap items-center gap-2 border-b border-neutral-200 bg-neutral-50 px-4 py-2.5">
                    <span className="text-xs text-muted-foreground">Subject</span>
                    <span className="flex-1 truncate text-sm font-medium text-foreground">
                      {previewSubject}
                    </span>
                    <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-600">
                      {isCustomised ? 'Customised' : 'Default'}
                    </span>
                    <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-mono uppercase text-neutral-600">
                      {defaultLocale}
                    </span>
                  </div>
                  <iframe
                    title="Confirmation email preview"
                    className="block w-full bg-white"
                    style={{ height: 220, border: 0 }}
                    sandbox=""
                    srcDoc={withStyleNonce(previewHtml)}
                  />
                </div>

                <p className="text-xs text-muted-foreground">
                  The confirm button leads to your{' '}
                  <Link
                    to="/settings/branding"
                    className="font-medium text-primary hover:underline"
                  >
                    branded confirmation page
                  </Link>{' '}
                  — logo, colour and languages come from Branding.
                </p>
              </CardContent>
            </Card>

            {/* Contacts added as already subscribed */}
            <Card className="rounded-lg border-neutral-200 bg-white shadow-sm">
              <CardContent className="flex flex-col gap-3 p-5 sm:p-6">
                <div className="flex items-center gap-2">
                  <SealCheck size={18} className="text-neutral-500" />
                  <h2 className="text-lg font-semibold text-foreground">
                    Contacts added as already subscribed
                  </h2>
                </div>
                <ul className="flex flex-col gap-2 pl-5 text-sm text-neutral-700 list-disc">
                  <li>
                    While double opt-in is on, adding a contact as{' '}
                    <span className="font-mono text-xs">subscribed</span> — by API, bulk import or
                    CSV — needs a <strong>stated reason</strong>, e.g. &quot;confirmed in our own
                    app&quot; or &quot;imported with consent from our trade-fair list&quot;. It is
                    saved with the contact&apos;s consent history. Without one, the API refuses the
                    request.
                  </li>
                  <li>
                    A contact who <strong>previously unsubscribed</strong> is never re-subscribed
                    directly. They get a fresh confirmation email, and only their click brings them
                    back.
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* SMS Double Opt-In card (MVP-3) */}
            <div className="flex flex-col gap-3 rounded-lg border-2 border-dashed border-neutral-300 bg-white p-5 sm:p-6">
              <div className="flex flex-wrap items-center gap-2">
                <ChatCircleText size={18} className="text-neutral-500" />
                <h2 className="text-lg font-semibold text-foreground">SMS double opt-in</h2>
                <Badge
                  variant="secondary"
                  className="bg-neutral-100 text-neutral-700 hover:bg-neutral-100"
                >
                  MVP-3
                </Badge>
              </div>
              <label className="flex cursor-not-allowed select-none items-center gap-2.5 opacity-60">
                <Switch checked={false} disabled aria-label="Require SMS confirmation" />
                <span className="text-sm text-foreground">Require SMS confirmation</span>
              </label>
              <p className="text-sm leading-6 text-muted-foreground">
                SMS sending itself is <strong>MVP-3</strong> - this toggle would only configure a
                setting today, with no live SMS confirmation behind it yet, so it stays disabled
                here rather than switching on something that cannot send.
              </p>
            </div>

            {/* Pending confirmations table */}
            <Card className="overflow-hidden rounded-lg border-neutral-200 bg-white shadow-sm">
              <div className="border-b border-neutral-200 p-4 sm:p-5">
                <h2 className="text-lg font-semibold text-foreground">Pending confirmations</h2>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  Contacts who have not yet clicked their confirmation link. None of these are
                  sendable.
                </p>
              </div>
              <Table className="min-w-[560px]">
                <TableHeader>
                  <TableRow className="bg-neutral-50 hover:bg-neutral-50">
                    <TableHead className="text-xs font-semibold text-muted-foreground">
                      Email
                    </TableHead>
                    <TableHead className="text-xs font-semibold text-muted-foreground">
                      Sent
                    </TableHead>
                    <TableHead className="text-xs font-semibold text-muted-foreground">
                      Resends
                    </TableHead>
                    <TableHead className="w-36 text-right" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingItems.length > 0 ? (
                    pendingItems.map((row) => {
                      const isResending = resendingIds.has(row.contactId);
                      const cooldown = cooldowns[row.contactId] ?? 0;
                      const isCooldown = cooldown > 0;

                      return (
                        <TableRow key={row.contactId}>
                          <TableCell className="font-medium text-foreground">{row.email}</TableCell>
                          <TableCell className="whitespace-nowrap text-muted-foreground">
                            {formatDateTime(row.sentAt)}
                          </TableCell>
                          <TableCell className="text-muted-foreground">{row.resendCount}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={isResending || isCooldown}
                              onClick={() => handleResend(row)}
                            >
                              {isResending
                                ? 'Resending...'
                                : isCooldown
                                  ? `Resend (${cooldown}s)`
                                  : 'Resend'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} className="py-12 text-center">
                        <CheckCircle size={28} className="mx-auto text-neutral-400" />
                        <p className="mt-2 font-medium text-foreground">No pending confirmations</p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          Every subscriber who signed up under double opt-in has confirmed.
                        </p>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {totalCount > PAGE_SIZE ? (
                <div className="flex items-center justify-between border-t border-neutral-200 px-4 py-3 sm:px-6">
                  <div className="text-xs text-muted-foreground">
                    Showing <span className="font-medium text-foreground">{offset + 1}</span> to{' '}
                    <span className="font-medium text-foreground">
                      {Math.min(offset + pendingItems.length, totalCount)}
                    </span>{' '}
                    of <span className="font-medium text-foreground">{totalCount}</span> pending
                    confirmations
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={!hasPrev || pendingQuery.isFetching}
                      onClick={() => setOffset((prev) => Math.max(0, prev - PAGE_SIZE))}
                    >
                      <CaretLeft size={14} className="mr-1" />
                      Previous
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={!hasNext || pendingQuery.isFetching}
                      onClick={() => setOffset((prev) => prev + PAGE_SIZE)}
                    >
                      Next
                      <CaretRight size={14} className="ml-1" />
                    </Button>
                  </div>
                </div>
              ) : null}
            </Card>
          </div>

          {serverErrorMessage ? (
            <div className="flex items-start gap-2.5 rounded-lg bg-destructive/10 px-3.5 py-3 text-sm text-destructive">
              <WarningCircle size={16} className="mt-0.5 shrink-0" />
              <p>{serverErrorMessage}</p>
            </div>
          ) : null}

          <div className="sticky bottom-0 flex flex-wrap items-center justify-end gap-3 bg-background pb-4 pt-2">
            {savedNote ? <span className="text-sm text-muted-foreground">{savedNote}</span> : null}
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? 'Saving...' : 'Save changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
